import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Brain, Search, Sparkles, Upload, Loader2, Plus, FileText, MessageSquare,
  CheckCircle2, Circle, ArrowUpRight, X, Archive, Trash2, Wand2, Layers,
  Bot, Cpu, Compass, Hash, StickyNote, FileType, Mic, Mail, Plug,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/UserAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Source library — each platform with status + export hint
// status: 'import' (works today via paste/file) | 'soon' (Phase 2 OAuth)
const SOURCES = [
  { id: 'chatgpt',    label: 'ChatGPT',    Icon: Bot,        status: 'import', hint: 'In ChatGPT → Settings → Data controls → Export data. Open the email link, unzip, then drag the conversations.json (or paste a single chat) here.' },
  { id: 'claude',     label: 'Claude',     Icon: Sparkles,   status: 'import', hint: 'In Claude → Settings → Privacy → Export data. Paste a conversation or upload the JSON file from the export.' },
  { id: 'gemini',     label: 'Gemini',     Icon: Cpu,        status: 'import', hint: 'In Gemini → Activity → Export. Paste the conversation text or upload the .txt/.json export.' },
  { id: 'perplexity', label: 'Perplexity', Icon: Compass,    status: 'import', hint: 'Copy any thread from Perplexity and paste here — the AI will summarize + cite it.' },
  { id: 'discord',    label: 'Discord',    Icon: Hash,       status: 'soon',   hint: 'Live Discord OAuth coming soon. For now, copy/paste the chat log.' },
  { id: 'notes',      label: 'Notes',      Icon: StickyNote, status: 'import', hint: 'Paste from Apple Notes, Notion, Obsidian, or any note app.' },
  { id: 'pdf',        label: 'PDFs',       Icon: FileType,   status: 'import', hint: 'Upload a .txt / .md / .json file, or paste extracted PDF text. (Native PDF parsing arrives in Phase 2.)' },
  { id: 'manual',     label: 'Documents',  Icon: FileText,   status: 'import', hint: 'Any doc — paste the contents or drop a text file.' },
  { id: 'voice',      label: 'Voice Notes',Icon: Mic,        status: 'soon',   hint: 'Auto-transcription via Whisper coming soon. For now, paste a transcript.' },
  { id: 'email',      label: 'Emails',     Icon: Mail,       status: 'soon',   hint: 'One-click "Send to Knowledge Base" from your inbox is coming. For now, paste the email body.' },
];

const Card = ({ children, className = '', ...props }) => (
  <div
    {...props}
    className={`bg-[#0C0E13] border border-white/10 rounded-xl p-5 transition-all hover:border-white/25 hover:shadow-[0_0_24px_rgba(255,255,255,0.06)] ${className}`}
  >
    {children}
  </div>
);

const Pill = ({ children, active = false, onClick, color }) => (
  <button
    onClick={onClick}
    style={color && active ? { background: color, color: '#0C0E13' } : undefined}
    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all border ${
      active
        ? 'bg-white text-black border-white'
        : 'bg-[#0C0E13] border-white/15 text-gray-300 hover:bg-[#161A22] hover:border-white/30'
    }`}
  >
    {children}
  </button>
);

const KnowledgeBase = () => {
  const { user } = useAuth();
  const userId = user?.id || user?._id;

  // Data
  const [brains, setBrains] = useState([]);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tasks, setTasks] = useState([]);

  // UI state
  const [activeBrain, setActiveBrain] = useState(null); // null = all
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modals
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadSource, setUploadSource] = useState('chatgpt');
  const [detailItem, setDetailItem] = useState(null);
  const [threadCategory, setThreadCategory] = useState(null);

  // Search
  const [query, setQuery] = useState('');
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState(null);

  const headers = { 'Content-Type': 'application/json' };

  const loadAll = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [b, i, c, t] = await Promise.all([
        fetch(`${API}/api/knowledge/brains?user_id=${userId}`).then(r => r.json()),
        fetch(`${API}/api/knowledge/items?user_id=${userId}`).then(r => r.json()),
        fetch(`${API}/api/knowledge/categories?user_id=${userId}`).then(r => r.json()),
        fetch(`${API}/api/knowledge/tasks?user_id=${userId}&completed=false`).then(r => r.json()),
      ]);
      setBrains(b.brains || []);
      setItems(i.items || []);
      setCategories(c.categories || []);
      setTasks(t.tasks || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [userId]);

  // Filter items by brain + category
  const filteredItems = useMemo(() => {
    return items.filter(it => {
      if (activeBrain && !(it.brain_slugs || []).includes(activeBrain)) return false;
      if (activeCategory && !(it.categories || []).includes(activeCategory)) return false;
      return true;
    });
  }, [items, activeBrain, activeCategory]);

  const insights = useMemo(() => {
    const out = [];
    for (const it of items) {
      for (const ins of (it.insights || [])) {
        out.push({ text: ins, item: it });
      }
    }
    return out.slice(0, 6);
  }, [items]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const askQuestion = async () => {
    if (!query.trim()) return;
    setAsking(true);
    setAnswer(null);
    try {
      const r = await fetch(`${API}/api/knowledge/ask`, {
        method: 'POST', headers,
        body: JSON.stringify({ user_id: userId, question: query, brain_slug: activeBrain }),
      });
      const data = await r.json();
      setAnswer(data);
    } catch (e) {
      toast.error('AI search failed');
    } finally {
      setAsking(false);
    }
  };

  const toggleTask = async (taskId, completed) => {
    setTasks(prev => prev.filter(t => t.id !== taskId));
    try {
      await fetch(`${API}/api/knowledge/tasks/${taskId}?user_id=${userId}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ completed: !completed }),
      });
    } catch {
      toast.error('Could not update task');
      loadAll();
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this item and its tasks?')) return;
    await fetch(`${API}/api/knowledge/items/${id}?user_id=${userId}`, { method: 'DELETE' });
    setDetailItem(null);
    loadAll();
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-black text-white p-6 md:p-10">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Brain size={28} className="text-white" />
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Knowledge Base</h1>
            </div>
            <p className="text-gray-400 text-sm max-w-2xl">
              Your AI-powered intelligence operating system. Drop conversations, notes, and documents — Claude organizes them into a self-evolving second brain.
            </p>
          </div>
          <button
            data-testid="kb-upload-btn"
            onClick={() => { setUploadSource('manual'); setUploadOpen(true); }}
            className="inline-flex items-center gap-2 bg-white text-black px-4 py-2.5 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            <Upload size={16} /> Upload conversation
          </button>
        </div>
      </div>

      {/* Connect Sources */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-[0.2em]">
            <Plug size={12} /> Connect Sources
          </div>
          <div className="text-xs text-gray-500">Click any platform to import</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {SOURCES.map(s => {
            const Icon = s.Icon;
            const isLive = s.status === 'import';
            return (
              <button
                key={s.id}
                onClick={() => {
                  if (!isLive) {
                    toast(`${s.label}: live connection coming soon. For now, paste the content manually.`);
                  }
                  setUploadSource(s.id);
                  setUploadOpen(true);
                }}
                className="group bg-[#0C0E13] border border-white/10 hover:border-white/30 hover:shadow-[0_0_22px_rgba(255,255,255,0.06)] rounded-xl p-4 text-left transition-all relative"
                data-testid={`kb-source-${s.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <Icon size={18} className="text-gray-200 group-hover:text-white" />
                  {isLive ? (
                    <span className="text-[9px] uppercase tracking-wider text-emerald-300 bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 rounded-full">Import</span>
                  ) : (
                    <span className="text-[9px] uppercase tracking-wider text-amber-300 bg-amber-400/10 border border-amber-400/20 px-2 py-0.5 rounded-full">Soon</span>
                  )}
                </div>
                <div className="text-sm font-semibold text-white mb-1">{s.label}</div>
                <div className="text-[11px] text-gray-500 leading-relaxed line-clamp-2">{s.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Universal AI Search */}
      <div className="max-w-7xl mx-auto mb-8">
        <Card>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-purple-300" />
            <h3 className="font-semibold text-sm tracking-wide">Ask your second brain</h3>
          </div>
          <div className="flex flex-col md:flex-row gap-2">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && askQuestion()}
                placeholder='e.g. "What were my best ideas about creator monetization?"'
                className="w-full pl-10 pr-4 py-3 bg-[#0C0E13] border border-white/15 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:border-white"
                data-testid="kb-search-input"
              />
            </div>
            <button
              onClick={askQuestion}
              disabled={asking || !query.trim()}
              className="px-5 py-3 bg-white text-black rounded-lg font-medium disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              data-testid="kb-ask-btn"
            >
              {asking ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
              {asking ? 'Thinking…' : 'Ask AI'}
            </button>
          </div>

          {answer && (
            <div className="mt-5 border-t border-white/10 pt-5">
              <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">
                {answer.answer}
              </div>
              {answer.sources?.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">Sources</div>
                  <div className="flex flex-wrap gap-2">
                    {answer.sources.map(s => (
                      <button
                        key={s.id}
                        onClick={() => openDetail(s.id, setDetailItem, userId)}
                        className="text-xs bg-[#161A22] border border-white/10 hover:border-white/30 px-3 py-1.5 rounded-full text-gray-300 hover:text-white"
                      >
                        {s.title}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* Multi-Brain switcher */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center gap-2 mb-3 text-gray-500 text-xs uppercase tracking-[0.2em]">
          <Layers size={12} /> AI Brains
        </div>
        <div className="flex flex-wrap gap-2">
          <Pill active={activeBrain === null} onClick={() => setActiveBrain(null)}>
            All ({items.length})
          </Pill>
          {brains.map(b => (
            <Pill
              key={b.slug}
              active={activeBrain === b.slug}
              color={b.color}
              onClick={() => setActiveBrain(activeBrain === b.slug ? null : b.slug)}
            >
              {b.label} ({b.item_count || 0})
            </Pill>
          ))}
        </div>
      </div>

      {/* Hub grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Recent Insights */}
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Recent Insights</h3>
            <span className="text-xs text-gray-500">{insights.length} insights</span>
          </div>
          {insights.length === 0 ? (
            <EmptyHint loading={loading} text="No insights yet — upload a conversation to start generating them." />
          ) : (
            <ul className="space-y-3">
              {insights.map((ins, idx) => (
                <li key={idx} className="text-sm text-gray-200 leading-relaxed flex gap-3">
                  <Sparkles size={14} className="text-purple-300 mt-1 flex-shrink-0" />
                  <div>
                    {ins.text}
                    <button
                      onClick={() => openDetail(ins.item.id, setDetailItem, userId)}
                      className="ml-2 text-xs text-gray-500 hover:text-white"
                    >
                      ({ins.item.title})
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Action Items */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Action Items</h3>
            <span className="text-xs text-gray-500">{tasks.length} open</span>
          </div>
          {tasks.length === 0 ? (
            <EmptyHint loading={loading} text="No open actions. AI extracts tasks from every upload." />
          ) : (
            <ul className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
              {tasks.slice(0, 10).map(t => (
                <li key={t.id} className="flex items-start gap-2 text-sm">
                  <button onClick={() => toggleTask(t.id, t.completed)} className="mt-0.5">
                    {t.completed ? <CheckCircle2 size={16} className="text-emerald-400" /> : <Circle size={16} className="text-gray-500 hover:text-white" />}
                  </button>
                  <span className={`flex-1 ${t.completed ? 'line-through text-gray-500' : 'text-gray-200'}`}>{t.text}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Dynamic Categories */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Dynamic Categories</h3>
          {activeCategory && (
            <button onClick={() => setActiveCategory(null)} className="text-xs text-gray-400 hover:text-white">
              Clear filter ×
            </button>
          )}
        </div>
        {categories.length === 0 ? (
          <Card><EmptyHint loading={loading} text="Categories will appear as the AI groups related conversations." /></Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {categories.map(cat => (
              <Card
                key={cat.name}
                className={`cursor-pointer ${activeCategory === cat.name ? 'border-white/40' : ''}`}
                onClick={() => setActiveCategory(activeCategory === cat.name ? null : cat.name)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="text-sm font-semibold leading-tight">{cat.name}</div>
                  <span className="text-xs text-gray-500">{cat.count}</span>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setThreadCategory(cat.name); }}
                  className="text-xs text-purple-300 hover:text-white inline-flex items-center gap-1"
                >
                  Synthesize Master Thread <ArrowUpRight size={12} />
                </button>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Items list */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">
            {activeCategory ? `Items in "${activeCategory}"` : 'Knowledge Items'}
          </h3>
          <span className="text-xs text-gray-500">{filteredItems.length} items</span>
        </div>
        {filteredItems.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <Brain size={36} className="mx-auto text-gray-600 mb-4" />
              <div className="text-sm text-gray-400 mb-4">
                {loading ? 'Loading your knowledge base…' : 'Your knowledge base is empty.'}
              </div>
              {!loading && (
                <button
                  onClick={() => { setUploadSource('manual'); setUploadOpen(true); }}
                  className="inline-flex items-center gap-2 bg-white text-black px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200"
                >
                  <Plus size={14} /> Upload your first conversation
                </button>
              )}
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredItems.map(it => (
              <Card key={it.id} className="cursor-pointer" onClick={() => openDetail(it.id, setDetailItem, userId)}>
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-sm leading-tight">{it.title}</h4>
                  <span className="text-[10px] uppercase tracking-wider text-gray-500 ml-2 flex-shrink-0">{it.source}</span>
                </div>
                <p className="text-xs text-gray-400 leading-relaxed line-clamp-3 mb-3">{it.summary}</p>
                <div className="flex flex-wrap gap-1.5">
                  {(it.brain_slugs || []).slice(0, 2).map(bs => {
                    const b = brains.find(x => x.slug === bs);
                    return b ? (
                      <span
                        key={bs}
                        style={{ background: `${b.color}20`, color: b.color, border: `1px solid ${b.color}40` }}
                        className="text-[10px] px-2 py-0.5 rounded-full"
                      >
                        {b.label.replace(' Brain', '')}
                      </span>
                    ) : null;
                  })}
                  {(it.tags || []).slice(0, 3).map(tg => (
                    <span key={tg} className="text-[10px] px-2 py-0.5 rounded-full bg-[#161A22] border border-white/10 text-gray-400">
                      #{tg}
                    </span>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Upload modal */}
      {uploadOpen && (
        <UploadModal
          userId={userId}
          initialSource={uploadSource}
          onClose={() => setUploadOpen(false)}
          onCreated={() => { setUploadOpen(false); loadAll(); }}
        />
      )}

      {/* Detail modal */}
      {detailItem && (
        <DetailModal
          item={detailItem}
          userId={userId}
          onClose={() => setDetailItem(null)}
          onDelete={() => deleteItem(detailItem.item.id)}
          onTaskToggle={(tid, completed) => {
            toggleTask(tid, completed);
            setDetailItem(prev => prev ? {
              ...prev,
              tasks: prev.tasks.map(t => t.id === tid ? { ...t, completed: !completed } : t),
            } : prev);
          }}
        />
      )}

      {/* Master Thread modal */}
      {threadCategory && (
        <ThreadModal
          category={threadCategory}
          userId={userId}
          onClose={() => setThreadCategory(null)}
        />
      )}
    </div>
  );
};

// ── Helpers / sub-components ──────────────────────────────────────────────
async function openDetail(itemId, setDetailItem, userId) {
  try {
    const r = await fetch(`${API}/api/knowledge/items/${itemId}?user_id=${userId}`);
    const data = await r.json();
    setDetailItem(data);
  } catch {
    toast.error('Failed to load item');
  }
}

const EmptyHint = ({ loading, text }) => (
  <div className="text-center py-6 text-gray-500 text-sm">
    {loading ? <Loader2 size={18} className="mx-auto animate-spin" /> : text}
  </div>
);

const UploadModal = ({ userId, onClose, onCreated, initialSource = 'chatgpt' }) => {
  const [title, setTitle] = useState('');
  const [source, setSource] = useState(initialSource);
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const activeSource = SOURCES.find(s => s.id === source);

  const onFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    setContent(text);
    if (!title) setTitle(file.name.replace(/\.[^.]+$/, ''));
  };

  const submit = async () => {
    if (!content.trim() || content.trim().length < 10) {
      toast.error('Paste or upload at least 10 characters of content.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/knowledge/ingest`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, title: title || null, source, content }),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success('Added to your knowledge base — AI is organizing it.');
      onCreated();
    } catch (e) {
      console.error(e);
      toast.error('Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-2xl bg-[#0C0E13] border border-white/15 rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold">Add to your second brain</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>

        <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1.5">Source</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {SOURCES.map(s => (
            <button
              key={s.id}
              onClick={() => setSource(s.id)}
              className={`px-3 py-1.5 rounded-full text-xs border ${source === s.id ? 'bg-white text-black border-white' : 'bg-[#161A22] text-gray-300 border-white/15 hover:border-white/30'}`}
            >
              {s.label}
            </button>
          ))}
        </div>
        {activeSource?.hint && (
          <div className="text-[11px] text-gray-400 bg-[#161A22] border border-white/10 rounded-lg px-3 py-2 mb-4 leading-relaxed">
            <span className="text-purple-300 font-medium">How to import {activeSource.label}: </span>
            {activeSource.hint}
          </div>
        )}

        <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1.5">Title (optional — AI will generate one)</label>
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="e.g. Marriott campaign brainstorm"
          className="w-full px-3 py-2.5 mb-4 bg-[#161A22] border border-white/15 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:border-white"
        />

        <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1.5">Conversation / note content</label>
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          rows={12}
          placeholder="Paste a full ChatGPT/Claude/Gemini conversation, a meeting transcript, a long note…"
          className="w-full px-3 py-2.5 mb-3 bg-[#161A22] border border-white/15 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:border-white font-mono"
        />

        <label className="block text-xs text-gray-500 mb-4">
          …or upload a .txt / .md / .json file{' '}
          <input
            type="file"
            accept=".txt,.md,.markdown,.json"
            onChange={e => onFile(e.target.files?.[0])}
            className="text-gray-400 ml-2"
          />
        </label>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
          <button
            onClick={submit}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-5 py-2 bg-white text-black rounded-lg text-sm font-medium disabled:opacity-50"
            data-testid="kb-ingest-submit"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {submitting ? 'AI organizing…' : 'Organize with AI'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DetailModal = ({ item, userId, onClose, onDelete, onTaskToggle }) => {
  const { item: it, related, tasks } = item;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-3xl bg-[#0C0E13] border border-white/15 rounded-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#0C0E13] border-b border-white/10 p-5 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{it.source}</div>
            <h2 className="text-lg font-semibold leading-snug">{it.title}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onDelete} className="text-gray-500 hover:text-red-400" title="Delete"><Trash2 size={16} /></button>
            <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          <section>
            <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Summary</h3>
            <p className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap">{it.summary}</p>
          </section>

          {it.insights?.length > 0 && (
            <section>
              <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Insights</h3>
              <ul className="space-y-2">
                {it.insights.map((ins, i) => (
                  <li key={i} className="text-sm text-gray-200 flex gap-2"><Sparkles size={14} className="text-purple-300 mt-0.5 flex-shrink-0" />{ins}</li>
                ))}
              </ul>
            </section>
          )}

          {tasks?.length > 0 && (
            <section>
              <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Action items</h3>
              <ul className="space-y-1.5">
                {tasks.map(t => (
                  <li key={t.id} className="flex items-start gap-2 text-sm">
                    <button onClick={() => onTaskToggle(t.id, t.completed)} className="mt-0.5">
                      {t.completed ? <CheckCircle2 size={16} className="text-emerald-400" /> : <Circle size={16} className="text-gray-500" />}
                    </button>
                    <span className={t.completed ? 'line-through text-gray-500' : 'text-gray-200'}>{t.text}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="flex flex-wrap gap-2">
            {it.categories?.map(c => <span key={c} className="text-xs px-2.5 py-1 rounded-full bg-[#161A22] border border-white/10 text-gray-300">{c}</span>)}
            {it.tags?.map(t => <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-[#161A22] border border-white/10 text-gray-400">#{t}</span>)}
          </section>

          {related?.length > 0 && (
            <section>
              <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Related</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {related.map(r => (
                  <div key={r.id} className="text-sm bg-[#161A22] border border-white/10 rounded-lg p-3">
                    <div className="font-medium text-gray-100 text-sm mb-1">{r.title}</div>
                    <div className="text-xs text-gray-500 line-clamp-2">{r.summary}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section>
            <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Original content</h3>
            <div className="text-xs text-gray-400 bg-[#161A22] border border-white/10 rounded-lg p-3 max-h-72 overflow-y-auto whitespace-pre-wrap font-mono leading-relaxed">
              {it.content}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

const ThreadModal = ({ category, userId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [thread, setThread] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/api/knowledge/threads/${encodeURIComponent(category)}?user_id=${userId}`);
        const data = await r.json();
        setThread(data);
      } catch {
        toast.error('Could not synthesize this thread');
      } finally {
        setLoading(false);
      }
    })();
  }, [category, userId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-2xl bg-[#0C0E13] border border-white/15 rounded-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#0C0E13] border-b border-white/10 p-5 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Master Thread</div>
            <h2 className="text-lg font-semibold">{thread?.thread?.title || category}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Loader2 size={28} className="animate-spin mb-3" />
              <div className="text-sm">Claude is synthesizing across {thread?.item_count || '…'} items…</div>
            </div>
          ) : !thread?.thread ? (
            <div className="text-sm text-gray-500">No data.</div>
          ) : (
            <>
              <section>
                <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Narrative</h3>
                <p className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap">{thread.thread.narrative}</p>
              </section>

              {thread.thread.key_decisions?.length > 0 && (
                <section>
                  <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Key decisions</h3>
                  <ul className="space-y-1.5 list-disc list-inside text-sm text-gray-200">
                    {thread.thread.key_decisions.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                </section>
              )}

              {thread.thread.open_questions?.length > 0 && (
                <section>
                  <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Open questions</h3>
                  <ul className="space-y-1.5 list-disc list-inside text-sm text-gray-200">
                    {thread.thread.open_questions.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                </section>
              )}

              {thread.thread.next_steps?.length > 0 && (
                <section>
                  <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Next steps</h3>
                  <ul className="space-y-1.5 list-disc list-inside text-sm text-gray-200">
                    {thread.thread.next_steps.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBase;
